import {create} from 'zustand';

export type ReportConfirmVariant = 'default' | 'danger';

export interface ReportConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ReportConfirmVariant;
  onConfirm: () => void;
  onCancel?: () => void;
}

export interface ReportPromptAnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type ReportPromptAnchor =
  | {type: 'rect'; rect: ReportPromptAnchorRect}
  | {type: 'element'; element: HTMLElement};

export interface ReportPromptField {
  id: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
}

interface ReportPromptOptionsBase {
  title: string;
  confirmLabel?: string;
  cancelLabel?: string;
  anchor?: ReportPromptAnchor;
  onCancel?: () => void;
}

export type ReportSinglePromptOptions = ReportPromptOptionsBase & {
  fields?: undefined;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  onSubmit: (value: string) => void;
};

export type ReportMultiPromptOptions = ReportPromptOptionsBase & {
  fields: ReportPromptField[];
  onSubmit: (values: Record<string, string>) => void;
};

export type ReportPromptOptions =
  | ReportSinglePromptOptions
  | ReportMultiPromptOptions;

type DialogState =
  | {type: 'confirm'; options: ReportConfirmOptions}
  | {type: 'prompt'; options: ReportPromptOptions}
  | null;

interface ReportDialogStore {
  dialog: DialogState;
  confirm: (options: ReportConfirmOptions) => void;
  prompt: (options: ReportPromptOptions) => void;
  close: () => void;
}

export const useReportDialogStore = create<ReportDialogStore>((set) => ({
  dialog: null,

  confirm: (options) => {
    set({dialog: {type: 'confirm', options}});
  },

  prompt: (options) => {
    set({dialog: {type: 'prompt', options}});
  },

  close: () => {
    set({dialog: null});
  }
}));

function promptDialog(options: ReportSinglePromptOptions): void;
function promptDialog(options: ReportMultiPromptOptions): void;
function promptDialog(options: ReportPromptOptions): void {
  useReportDialogStore.getState().prompt(options);
}

/** Imperative helpers for use outside React components */
export const reportDialog = {
  confirm: (options: ReportConfirmOptions): void => {
    useReportDialogStore.getState().confirm(options);
  },
  prompt: promptDialog,
  close: (): void => {
    useReportDialogStore.getState().close();
  }
};
